import { useEffect, useState } from 'react';
type Job = { ym: string; status: string; rows: number; total_lines: number | null; updated_at: number };
type Status = { connected: boolean; has_creds: boolean; redirect: string; jobs: Job[] };

export default function CdrReports() {
  const [st, setSt] = useState<Status | null>(null);
  const [cid, setCid] = useState('');
  const [csec, setCsec] = useState('');
  const [bearer, setBearer] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() { const r = await fetch('/cdr/api/reports', { credentials: 'include' }); if (r.ok) setSt(await r.json()); else if (r.status === 403) setMsg('Admins only.'); }
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  async function saveCreds() {
    if (!cid.trim() || !csec.trim()) { setMsg('Enter both Client ID and Secret.'); return; }
    setBusy(true); setMsg(null);
    try { const r = await fetch('/cdr/api/reports/secret', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ client_id: cid.trim(), client_secret: csec.trim() }) });
      setMsg(r.ok ? 'Saved. Now click Connect.' : 'Save failed.'); setCsec(''); load();
    } finally { setBusy(false); }
  }
  async function saveBearer() {
    if (!bearer.trim()) { setMsg('Paste your personal access token first.'); return; }
    setBusy(true); setMsg(null);
    try { const r = await fetch('/cdr/api/reports/secret', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ bearer: bearer.trim() }) });
      setMsg(r.ok ? 'Token saved — click Backfill Jan→present.' : 'Save failed.'); setBearer(''); load();
    } finally { setBusy(false); }
  }
  async function act(path: string, note: string) {
    setBusy(true); setMsg(note + '…');
    try { const r = await fetch(path, { method: 'POST', credentials: 'include' }); const j = await r.json().catch(() => ({}));
      setMsg(j.error ? ('Error: ' + j.error) : (note + ' ✓')); load();
    } catch (e: any) { setMsg('Error: ' + (e?.message || e)); } finally { setBusy(false); }
  }
  const inp = 'px-2.5 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs';
  const badge = (s: string) => {
    const c = s === 'imported' ? 'bg-green-600/15 text-green-700 dark:text-green-400' : s === 'failed' ? 'bg-red-600/15 text-red-700 dark:text-red-400' : 'bg-gray-500/15 text-gray-600 dark:text-gray-300';
    return <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${c}`}>{s}</span>;
  };
  return (
    <div className="card p-3 space-y-3">
      <div className="text-sm font-semibold">Historical Import</div>
      {!st ? <div className="text-xs text-gray-500">{msg || 'Loading…'}</div> : (
        <>
          {!st.connected && (
            <div className="space-y-3">
              <div className="rounded-lg border border-tnh-border dark:border-tnh-border-dark p-2.5 space-y-1.5">
                <div className="text-xs font-medium">Quickest: paste your 12‑hour personal access token</div>
                <div className="text-[11px] text-gray-500">From developer.webex.com (Getting Started → Copy your access token). Uses your own admin login. Good for the one‑time backfill; it expires in ~12h and won't auto‑import future months.</div>
                <div className="flex flex-wrap gap-2 items-center">
                  <input type="password" value={bearer} onChange={(e) => setBearer(e.target.value)} placeholder="Personal access token" className={inp + ' flex-1 min-w-[260px]'} autoComplete="off" />
                  <button onClick={saveBearer} disabled={busy} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs">Save token &amp; enable</button>
                </div>
              </div>
              <div className="text-[11px] text-gray-500 pt-1">— or set up a durable Integration (auto‑refresh, future months) —</div>
              <div className="text-xs text-gray-500">Create a Webex <b>Integration</b> (developer.webex.com → My Webex Apps → Create an Integration) with redirect URI <code className="text-tnh-accent">{st.redirect}</code> and scope <code>spark-admin:reports_read</code>, then paste its Client ID + Secret:</div>
              <div className="flex flex-wrap gap-2 items-center">
                <input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="Client ID" className={inp + ' flex-1 min-w-[220px]'} />
                <input type="password" value={csec} onChange={(e) => setCsec(e.target.value)} placeholder="Client Secret" className={inp + ' flex-1 min-w-[220px]'} autoComplete="off" />
                <button onClick={saveCreds} disabled={busy} className="px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-xs">Save credentials</button>
                <a href="/reports/connect" target="_blank" rel="noreferrer" className={`px-3 py-1.5 rounded-md text-xs text-white ${st.has_creds ? 'bg-tnh-accent' : 'bg-gray-400 pointer-events-none'}`}>Connect Webex Reports</a>
              </div>
            </div>
          )}
          {st.connected && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="px-1.5 py-0.5 rounded bg-green-600/15 text-green-700 dark:text-green-400 text-[11px] font-medium">Connected ✓</span>
              <button onClick={() => act('/cdr/api/reports/backfill', 'Requesting monthly reports Jan→present')} disabled={busy} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs disabled:opacity-50">Backfill Jan→present</button>
              <button onClick={() => act('/cdr/api/reports/run', 'Running import step')} disabled={busy} className="px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800 text-xs disabled:opacity-50">Run import step</button>
              <span className="text-[11px] text-gray-400">Requesting creates a report per month; import steps download &amp; load them (repeat, or the hourly job continues on its own).</span>
            </div>
          )}
          {msg && <div className="text-xs text-gray-500">{msg}</div>}
          {st.jobs.length > 0 && (
            <table className="w-full text-xs">
              <thead className="text-gray-500"><tr><th className="text-left px-2 py-1">Month</th><th className="text-left px-2 py-1">Status</th><th className="text-right px-2 py-1">Rows loaded</th><th className="text-right px-2 py-1">Updated</th></tr></thead>
              <tbody>
                {st.jobs.map((jb) => (
                  <tr key={jb.ym} className="border-t border-tnh-border/40 dark:border-tnh-border-dark/40"><td className="px-2 py-1 font-mono">{jb.ym}</td><td className="px-2 py-1">{badge(jb.status)}</td><td className="px-2 py-1 text-right tabular-nums">{jb.rows?.toLocaleString?.() ?? jb.rows}</td><td className="px-2 py-1 text-right text-gray-400">{jb.updated_at ? new Date(jb.updated_at).toLocaleTimeString() : '—'}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
